import { gql } from '@apollo/client';

import { VenueFragment } from '@/fragments/venue-fragment';
import { ERegistrationType, EventVenueType } from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { useReactiveEventTiming } from '@/hooks/useReactiveEventTiming';

import { UseEventStuff_EventFragment } from './graphql-types/UseEventStuff_EventFragment';

// Helper to check if venue has valid Agora Meet configuration
const hasValidAgoraMeetConfig = (venue: any): boolean => {
  if (!venue?.online) return false;
  if (venue.online.__typename !== 'EventVenue_TradewingPlatform') return false;
  if (!venue.online.agoraMeetId) return false;
  if (!venue.online.stream) return false;
  if (venue.online.stream.__typename !== 'EventVenue_TradewingPlatform_Stream_Agora_Video_Call') return false;
  if (!venue.online.stream.agoraMeetSettings) return false;

  return true;
};

const EVENT_FRAGMENT = gql`
  fragment UseEventStuff_EventFragment on EventV2 {
    _id
    name
    registrationType
    startDate
    endDate
    isCurrentUserSpeaker
    status
    hasTickets
    ticketsCurrentUserCanPurchase {
      _id
    }
    currentValidUserRegistration {
      _id
    }
    isExternalRegistrationUrlActive
    ... on SimpleEventV2 {
      hasCanonicalVideo
      canCurrentUserJoinEventEarly
      currentUserRsvp {
        _id
      }
      venue {
        ...VenueFragment
      }
    }
    ... on ConferenceEventV2 {
      venueType
      isCurrentUserAudienceGroupAdmin
      isCurrentUserSpeaker
      currentUserHostedBoothIds
      hasSessionWithCanonicalVideo
    }
  }
  ${VenueFragment}
`;

export const useEventStuff = (event: UseEventStuff_EventFragment) => {
  const { isEventPast, isEventLive, isEventFuture } = useReactiveEventTiming({
    startDate: new Date(event.startDate),
    endDate: new Date(event.endDate),
  });

  const permissions = usePermissions();

  // Check if event has valid Agora Meet configuration
  const isAgoraMeetEvent = event.__typename === 'SimpleEventV2' && 
    event.venue?.online?.__typename === 'EventVenue_TradewingPlatform' &&
    event.venue.online.stream?.__typename === 'EventVenue_TradewingPlatform_Stream_Agora_Video_Call';
 
  const hasValidAgoraMeetSettings = isAgoraMeetEvent ? hasValidAgoraMeetConfig(event.venue) : true;

  return {
    isExternalRegistrationUrlActive: !!event.isExternalRegistrationUrlActive,
    isEventPast,
    isEventLive,
    isEventFuture,
    isCurrentUserRegistered: !!event.currentValidUserRegistration,
    didCurrentUserRsvp:
      event.__typename === 'SimpleEventV2' && !!event.currentUserRsvp,
    areTicketsAvailableForCurrentUser:
      event.ticketsCurrentUserCanPurchase.length > 0,
    isEventTicketed: event.registrationType === ERegistrationType.TICKET,
    isEventOpen: event.registrationType === ERegistrationType.OPEN,
    hasCanonicalVideo:
      event.__typename === 'SimpleEventV2' ? event.hasCanonicalVideo : false,
    hasSessionWithCanonicalVideo:
      event.__typename === 'ConferenceEventV2'
        ? event.hasSessionWithCanonicalVideo
        : false,
    isConference: event.__typename === 'ConferenceEventV2',
    isSimpleEvent: event.__typename === 'SimpleEventV2',
    canCurrentUserJoinEventEarly:
      event.__typename === 'SimpleEventV2'
        ? (event.canCurrentUserJoinEventEarly && (!isAgoraMeetEvent || hasValidAgoraMeetSettings))
        : event.__typename === 'ConferenceEventV2'
        ? permissions.canDoAdminStuffAndAuthenticated ||
          event.currentUserHostedBoothIds.length > 0 ||
          event.isCurrentUserAudienceGroupAdmin ||
          event.isCurrentUserSpeaker
        : false,
    isCurrentUserSpeaker: event.isCurrentUserSpeaker,
    status: event.status,
    hasTickets: event.hasTickets,
    isAtLeastPartiallyOnline:
      (event.__typename === 'SimpleEventV2' &&
        (event.venue.type === EventVenueType.ONLINE ||
          event.venue.type === EventVenueType.HYBRID)) ||
      (event.__typename === 'ConferenceEventV2' &&
        (event.venueType === EventVenueType.ONLINE ||
          event.venueType === EventVenueType.HYBRID)),
    // Add these fields to help consumers handle Agora Meet events
    isAgoraMeetEvent,
    meetId: isAgoraMeetEvent && event.venue?.online?.__typename === 'EventVenue_TradewingPlatform' ? event.venue.online.agoraMeetId : null,
    hasValidAgoraMeetSettings,
  };
};

useEventStuff.eventFragment = EVENT_FRAGMENT;
